import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { NestApi } from '../api/axiosInstance';
import { validatePdfFileBytes } from '../../utils/fileValidation';

interface InterviewAnswer {
  questionText: string;
  answerText: string;
}

interface JobApplicationState {
    applicationData: any;
    hasApplied: boolean;
    loading: boolean;
    success: boolean;
    error: string | null;
    appliedJobs: any[];
    appliedJobsLoading: boolean;
    appliedJobsError: string | null;
    currentPage: number;
    totalCount: number;
    limit: number;
}

interface FetchAppliedJobsParams {
    candidate_id: number;
    page?: number;
    limit?: number;
}

interface FetchAppliedJobsResponse {
    jobs: any[];
    totalCount: number;
    currentPage: number;
    limit: number;
}
// Thunk: Apply for job
export const applyForJob = createAsyncThunk<
    any, // return type
    { file?: File; job_id: number; candidate_id?: number }, 
    { rejectValue: string }
>('job/applyForJob', async (payload, { rejectWithValue }) => {
    try {
        let response;

        if (payload.file) {
            // Safer magic byte validation
            const result = await validatePdfFileBytes(payload.file);
            if (result !== true) {
                return rejectWithValue(result);
            }
            
            const formData = new FormData();
            formData.append('resume', payload.file);
            formData.append('job_id', payload.job_id.toString());

            response = await NestApi.post('/jobs/apply', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        } else if (payload.candidate_id) {
            response = await NestApi.post('/jobs/apply', {
                candidate_id: payload.candidate_id,
                job_id: payload.job_id,
            });
        } else {
            return rejectWithValue('Missing required data: resume file or candidate_id');
        }

        const data = response.data?.data ?? response.data;
        localStorage.setItem('intQuestion', JSON.stringify(data));
        return data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || error.response?.data || 'Failed to apply for job');
    }
});

// Thunk: Submit interview answers
export const submitInterviewAnswers = createAsyncThunk<
    any,
    { jobId: number; candidateId: number; answers: InterviewAnswer[] },
    { rejectValue: string }
>('job/submitInterviewAnswers', async ({ jobId, candidateId, answers }, { rejectWithValue }) => {
    try {
        const payload = { candidateId, jobId, answers };
        const response = await NestApi.put('/resume-parser/answers', payload);
        return response.data?.data ?? response.data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data || 'Failed to submit interview answers');
    }
});

// Thunk: Check application status
export const checkJobApplicationStatus = createAsyncThunk<
    boolean,
    { candidateId: number; jobId: number },
    { rejectValue: string }
>('job/checkJobApplicationStatus', async ({ candidateId, jobId }, { rejectWithValue }) => {
    try {
        const response = await NestApi.get(
            `/jobs/forms/${candidateId}?job_id=${jobId}`
        );
        const data = response.data?.data ?? response.data;
        return data.applied;
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Check failed');
    }
});

export const fetchAppliedJobs = createAsyncThunk<FetchAppliedJobsResponse, FetchAppliedJobsParams, { rejectValue: string }>(
    'job/fetchAppliedJobs', 
    async ({ candidate_id, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
        const response = await NestApi.get(`/jobs/${candidate_id}/applied-jobs`);
        const jobs = response.data?.data ?? response.data;
        
        return {
            jobs: Array.isArray(jobs) ? jobs : [],
            totalCount: Array.isArray(jobs) ? jobs.length : 0,
            currentPage: page,
            limit: limit,
        };
 
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Error fetching applied jobs');
    }
});
// Initial State
const initialState: JobApplicationState = {
    applicationData: null,
    hasApplied: false,
    loading: false,
    success: false,
    error: null,
    appliedJobs: [],
    appliedJobsLoading: false,
    appliedJobsError: null,
    currentPage: 1,
    totalCount: 0,
    limit: 10,
    
};

// Slice
const jobApplicationSlice = createSlice({
    name: 'jobApplication',
    initialState,
    reducers: {
        resetJobApplicationState: (state) => {
            state.applicationData = null;
            state.loading = false;
            state.success = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(applyForJob.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(applyForJob.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.applicationData = action.payload;
                localStorage.setItem('intQuestion', JSON.stringify(action.payload));
            })
            .addCase(applyForJob.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Application failed';
                state.success = false;
            })

            .addCase(submitInterviewAnswers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(submitInterviewAnswers.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(submitInterviewAnswers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Submit failed';
            })

            .addCase(checkJobApplicationStatus.fulfilled, (state, action) => {
                state.hasApplied = action.payload;
            })
            .addCase(checkJobApplicationStatus.rejected, (state) => {
                state.hasApplied = false;
            })

            .addCase(fetchAppliedJobs.pending, (state) => {
                state.appliedJobsLoading = true;
                state.appliedJobsError = null;
            })
            .addCase(fetchAppliedJobs.fulfilled, (state, action) => {
                state.appliedJobsLoading = false;
                 state.appliedJobs = action.payload.jobs;
                state.totalCount = action.payload.totalCount;
                state.currentPage = action.payload.currentPage;
                state.limit = action.payload.limit;
            })
            .addCase(fetchAppliedJobs.rejected, (state, action) => {
                state.appliedJobsLoading = false;
                state.appliedJobsError = action.payload || 'Error';
            });
    },
});

export const { resetJobApplicationState } = jobApplicationSlice.actions;
export default jobApplicationSlice.reducer;
